import type { Tables } from '../../types/database.types';

/**
 * Brief links: the shapes and the decisions, with no React in them.
 *
 * ── The constraint everything here bends around ────────────────────────────
 *
 * `list_brief_tokens()` returns `right(token, 6)` and never the token. A live
 * token is handed back exactly once, by `create_brief_token`, and can never be
 * read again. That is deliberate (20270140000100) and it is why this module
 * has two different row types instead of one: what you get when you mint is
 * not what you get when you list, and collapsing them into a single optional
 * `token?: string` would be an invitation to store the full one somewhere.
 */

/** One row of the list. Carries a tail, never a token. */
export interface BriefLinkRow {
  token_tail: string;
  label: string;
  created_at: string;
  revoked_at: string | null;
  last_seen_at: string | null;
  seen_count: number;
}

/** What minting returns: the whole row, token included, once. */
export type MintedBriefToken = Tables<'brief_tokens'>;

/** Live means not revoked. There is no expiry; see the design note. */
export function isLive(row: BriefLinkRow): boolean {
  return row.revoked_at === null;
}

/**
 * Live links first, then dead ones, each group newest first.
 *
 * A revoked link stays on the list rather than disappearing, because "did I
 * already kill that one" is a question somebody asks, and an empty space is
 * not an answer. It just stops competing for attention with the live ones.
 */
export function sortedLinks(rows: readonly BriefLinkRow[]): BriefLinkRow[] {
  return [...rows].sort((a, b) => {
    if (isLive(a) !== isLive(b)) {
      return isLive(a) ? -1 : 1;
    }
    return b.created_at.localeCompare(a.created_at);
  });
}

/** A label somebody left blank still has to be nameable on screen. */
export function linkName(row: BriefLinkRow): string {
  const label = row.label.trim();
  return label === '' ? `Link ending ${row.token_tail}` : label;
}

/**
 * What a row says about use, in a sentence.
 *
 * The table holds `last_seen_at` and `seen_count` and nothing finer, because
 * the comment on it says this is the CEO's reading habit and not an audit
 * trail. This repeats the same two numbers and adds nothing. The reason to
 * show them at all: a link opened far more than one person could is the only
 * sign it has spread, and revoking is the only control. A number nobody can
 * see is not a control.
 */
export function usageLine(row: BriefLinkRow, now: Date): string {
  if (row.seen_count === 0) {
    return 'Never opened';
  }
  const times = row.seen_count === 1 ? 'Opened once' : `Opened ${row.seen_count} times`;
  if (row.last_seen_at === null) {
    return times;
  }
  return `${times}, last ${dayLabel(row.last_seen_at, now)}`;
}

/** Today, yesterday, or the date. Same ladder the brief notes use. */
export function dayLabel(iso: string, now: Date): string {
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) {
    return 'at an unknown time';
  }
  const days = wholeDaysBetween(then, now);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  return then.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

function wholeDaysBetween(then: Date, now: Date): number {
  const startOf = (d: Date) => Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
  return Math.round((startOf(now) - startOf(then)) / 86400000);
}

/**
 * The state of the reveal after a mint.
 *
 * `null` is "nothing to show", which is also what a reload gives you, because
 * the token was never stored anywhere to reload from. The panel renders this
 * and only this; it has no way to get back to a token it has dismissed, and
 * that is the property worth protecting.
 */
export interface Reveal {
  token: string;
  name: string;
}

/** The reveal for a freshly minted token, or null if minting gave us none. */
export function revealFor(minted: MintedBriefToken | null): Reveal | null {
  if (minted === null || minted.token.trim() === '') {
    return null;
  }
  return {
    token: minted.token,
    name: minted.label.trim() === '' ? 'New link' : minted.label.trim(),
  };
}
